/**
 * Root wallpaper host — loads kind:wallpaper packages into a full-bleed layer.
 * Falls back to built-in aurora when the package is missing, fails, or no-ops.
 */
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { osStore } from "../store";
import { createModuleContext } from "../api";

type WpApi = {
  mount?: (
    el: HTMLElement,
    ctx: unknown
  ) => void | (() => void) | Promise<void | (() => void)>;
  unmount?: () => void;
  setPaused?: (p: boolean) => void;
};

function mountBuiltinAurora(host: HTMLElement): {
  dispose: () => void;
  setPaused: (p: boolean) => void;
} {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(host.clientWidth || 1, host.clientHeight || 1, false);
  renderer.setClearColor(0x07080f, 1);
  host.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const uniforms = {
    uTime: { value: 0 },
    uRes: {
      value: new THREE.Vector2(host.clientWidth || 1, host.clientHeight || 1),
    },
    uPaused: { value: 0 },
  };

  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      varying vec2 vUv;
      uniform float uTime;
      uniform vec2 uRes;
      uniform float uPaused;
      float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453); }
      float noise(vec2 p){
        vec2 i = floor(p); vec2 f = fract(p);
        float a = hash(i);
        float b = hash(i + vec2(1.0, 0.0));
        float c = hash(i + vec2(0.0, 1.0));
        float d = hash(i + vec2(1.0, 1.0));
        vec2 u = f*f*(3.0-2.0*f);
        return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
      }
      void main() {
        vec2 uv = vUv;
        float t = uTime * (1.0 - uPaused);
        vec2 p = uv * vec2(uRes.x/uRes.y, 1.0);
        float n1 = noise(p * 1.5 + vec2(t*0.05, t*0.03));
        float n2 = noise(p * 2.8 + vec2(-t*0.04, t*0.06));
        float n3 = noise(p * 5.0 + vec2(t*0.02, -t*0.05));
        float band = smoothstep(0.25, 0.75, n1 * 0.55 + n2 * 0.35 + n3 * 0.2);
        vec3 deep = vec3(0.03, 0.04, 0.10);
        vec3 mid  = vec3(0.08, 0.18, 0.36);
        vec3 hi   = vec3(0.35, 0.55, 0.95);
        vec3 mist = vec3(0.55, 0.35, 0.75);
        vec3 col = mix(deep, mid, band);
        col = mix(col, hi, smoothstep(0.55, 0.95, n2));
        col = mix(col, mist, 0.25 * smoothstep(0.4, 0.9, n3) * (0.5 + 0.5*sin(t*0.2)));
        float v = smoothstep(1.2, 0.2, length(uv - 0.5));
        col *= 0.55 + 0.45 * v;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });

  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  scene.add(quad);

  let raf = 0;
  const start = performance.now();
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const onResize = () => {
    const w = host.clientWidth || 1;
    const h = host.clientHeight || 1;
    renderer.setSize(w, h, false);
    uniforms.uRes.value.set(w, h);
  };
  window.addEventListener("resize", onResize);

  const setPaused = (p: boolean) => {
    uniforms.uPaused.value = p || document.hidden || reduced ? 1 : 0;
  };

  const onVis = () => setPaused(osStore.wallpaperPaused);
  document.addEventListener("visibilitychange", onVis);
  const unsub = osStore.subscribe(onVis);
  onVis();

  const tick = () => {
    raf = requestAnimationFrame(tick);
    if (!uniforms.uPaused.value) {
      uniforms.uTime.value = (performance.now() - start) / 1000;
    }
    renderer.render(scene, camera);
  };
  tick();

  return {
    setPaused,
    dispose: () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVis);
      unsub();
      mat.dispose();
      quad.geometry.dispose();
      renderer.dispose();
      if (renderer.domElement.parentElement === host) {
        host.removeChild(renderer.domElement);
      }
    },
  };
}

async function loadWallpaperModule(id: string): Promise<WpApi | null> {
  try {
    const url = `/modules/${id}/index.js?t=${Date.now()}`;
    const mod = (await import(/* @vite-ignore */ url)) as {
      default?: WpApi;
    } & WpApi;
    return (mod.default || mod) as WpApi;
  } catch {
    return null;
  }
}

function hostLooksPainted(host: HTMLElement): boolean {
  if (host.childElementCount > 0) return true;
  const bg = (host.style.backgroundImage || "").trim();
  if (bg && bg !== "none") return true;
  const solid = (host.style.background || host.style.backgroundColor || "").trim();
  if (solid && solid !== "transparent" && solid !== "rgba(0, 0, 0, 0)") return true;
  return false;
}

function WallpaperMount({ wallpaperId }: { wallpaperId: string }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    const host = hostRef.current;
    if (!host) return;

    let dispose: (() => void) | undefined;

    const run = async () => {
      host.innerHTML = "";
      host.style.backgroundImage = "";
      host.style.background = "";
      host.style.backgroundColor = "";

      const id = wallpaperId || "wallpaper.aurora";
      const ctx = createModuleContext({ appId: id });
      const api = await loadWallpaperModule(id);
      if (!alive) return;

      if (api?.mount) {
        try {
          const ret = await Promise.resolve(api.mount(host, ctx));
          // wallpaper.aurora intentionally no-ops and expects host builtin paint.
          if (hostLooksPainted(host)) {
            const parts: Array<() => void> = [];
            if (typeof ret === "function") parts.push(ret);
            parts.push(() => api.unmount?.());
            const applyPause = () => api.setPaused?.(osStore.wallpaperPaused);
            applyPause();
            parts.push(osStore.subscribe(applyPause));
            dispose = () => {
              for (const p of parts) {
                try {
                  p();
                } catch {
                  /* */
                }
              }
            };
            return;
          }
          host.innerHTML = "";
        } catch (e) {
          /* Settings are NOT cleared here, and must never be: a bad value can
             make mount() throw, and wiping the bag would turn "that setting is
             too high" into "all my settings vanished" with no way back. The
             stored values stay put so the user can lower the offending one. */
          console.warn(
            "[wallpaper] mount failed, keeping settings and falling back to aurora",
            id,
            e
          );
          host.innerHTML = "";
        }
      }

      const builtin = mountBuiltinAurora(host);
      const applyPause = () => builtin.setPaused(osStore.wallpaperPaused);
      applyPause();
      const unsub = osStore.subscribe(applyPause);
      dispose = () => {
        unsub();
        builtin.dispose();
      };
    };

    void run();
    return () => {
      alive = false;
      dispose?.();
      host.innerHTML = "";
    };
  }, [wallpaperId]);

  return (
    <div
      className="os-canvas-host"
      ref={hostRef}
      data-wallpaper={wallpaperId}
    />
  );
}

/**
 * Reactive wallpaper layer — remounts when the active wallpaper id changes,
 * or when a `reload: true` setting is committed.
 *
 * The nonce rides the React key so React owns the teardown: no hand-rolled
 * generation counter, and `WallpaperMount`'s existing `alive` flag already
 * stops a stale async mount from winning.
 */
export function WallpaperLayer() {
  const [id, setId] = useState(osStore.wallpaperId);
  const [nonce, setNonce] = useState(osStore.wallpaperReloadNonce);
  useEffect(
    () =>
      osStore.subscribe(() => {
        setId(osStore.wallpaperId);
        setNonce(osStore.wallpaperReloadNonce);
      }),
    []
  );
  return <WallpaperMount key={`${id}:${nonce}`} wallpaperId={id} />;
}

// Back-compat name
export const ThreeCanvasHost = WallpaperLayer;
